import React, { useState } from "react";
import { Trophy, XCircle, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

export default function MartingaleTracker({ signal, onUpdated, onWin }) {
  const [loading, setLoading] = useState(false);
  const { t } = useLang();
  if (!signal) return null;
  const steps = signal.martingale || [];
  const status = signal.status;
  const activeIdx = steps.findIndex((s) => s.result == null);

  const send = async (step, result) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/signals/${signal.id}/martingale`, { step, result });
      onUpdated?.(data);
      if (result === "win") {
        toast.success(t("mart.toastWin"));
        onWin?.();
      } else if (data.status === "lost") {
        toast.error(t("mart.toastLostAll"));
      } else {
        toast.message(t("mart.toastStep", { s: step, n: step + 1 }));
      }
    } catch (e) {
      toast.error(t("mart.toastErr"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#243047] bg-[#121824] p-5" data-testid="martingale-tracker">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-white">{t("mart.title")}</h3>
        <StatusBadge status={status} t={t} />
      </div>
      <p className="text-[11px] text-[#94A3B8] mb-4" data-testid="martingale-rule">{t("mart.rule")}</p>

      <div className="grid grid-cols-3 gap-3">
        {steps.map((s, i) => {
          const done = s.result != null;
          const isActive = i === activeIdx && status === "active";
          const won = s.result === "win";
          return (
            <div key={s.step} className={`rounded-lg border p-3 transition-all ${isActive ? "border-[#EC1E9C] bg-[#EC1E9C]/5" : "border-[#243047] bg-[#0A0D14]"} ${done ? "opacity-90" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#64748B]">{t("mart.step")} {s.step}</div>
              <div className="font-mono font-bold text-white text-lg">x{s.multiplier}</div>
              <div className="font-mono text-[11px] text-[#94A3B8] mb-2">{t("mart.stake")} {s.stake}</div>
              {done ? (
                <div className={`text-xs font-bold font-mono ${won ? "text-[#00FFC2]" : "text-[#EC1E9C]"}`}>{won ? t("mart.won") : t("mart.lost")}</div>
              ) : isActive ? (
                <div className="flex gap-1.5" data-testid={`martingale-step-${s.step}-actions`}>
                  <button disabled={loading} data-testid={`martingale-step-${s.step}-win`} onClick={() => send(s.step, "win")}
                    className="flex-1 rounded-md bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-[11px] font-bold py-1.5 hover:brightness-110 transition">
                    {t("mart.win")}
                  </button>
                  <button disabled={loading} data-testid={`martingale-step-${s.step}-loss`} onClick={() => send(s.step, "loss")}
                    className="flex-1 rounded-md bg-[#EC1E9C] text-white text-[11px] font-bold py-1.5 hover:brightness-110 transition">
                    {t("mart.loss")}
                  </button>
                </div>
              ) : (
                <div className="text-[11px] text-[#64748B] font-mono">{t("mart.waiting")}</div>
              )}
            </div>
          );
        })}
      </div>

      {status === "won" && (
        <button data-testid="martingale-generate-new" onClick={onWin}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-2.5 hover:brightness-110 transition">
          <RefreshCw className="w-4 h-4" /> {t("mart.newSignal")}
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    active: { t: t("mart.statusActive"), c: "#EC1E9C", I: RefreshCw },
    won: { t: t("mart.statusWon"), c: "#00FFC2", I: Trophy },
    lost: { t: t("mart.statusLost"), c: "#EC1E9C", I: XCircle },
  };
  const s = map[status] || map.active;
  const I = s.I;
  return (
    <span className="flex items-center gap-1.5 text-xs font-bold font-mono px-2.5 py-1 rounded-md border"
      style={{ color: s.c, borderColor: s.c, background: `${s.c}12` }} data-testid="martingale-status">
      <I className="w-3.5 h-3.5" /> {s.t}
    </span>
  );
}
